"use client";

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from "react";
import { useRouter, usePathname } from "next/navigation";
import {
  identifyPro,
  setRetailerGroup,
  resetAnalytics,
  capture,
} from "@/lib/analytics/client";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";

export interface ProUser {
  userId: string;
  email: string;
  retailerId: string;
  retailerName?: string;
}

interface ProAuthContextValue {
  user: ProUser | null;
  accessToken: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  getAuthHeaders: () => Record<string, string>;
  fetchWithAuth: (url: string, options?: RequestInit) => Promise<Response>;
}

const ProAuthContext = createContext<ProAuthContextValue | null>(null);

const TOKEN_KEY = "pro_access_token";
const REFRESH_KEY = "pro_refresh_token";
const USER_KEY = "pro_user";

export function ProAuthProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  // Start with null/true so SSR and first client render match (both render nothing)
  const [user, setUser] = useState<ProUser | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const refreshPromiseRef = useRef<Promise<string | null> | null>(null);

  // Restore session from localStorage after mount (client-only)
  useEffect(() => {
    try {
      const storedToken = localStorage.getItem(TOKEN_KEY);
      const storedUser = localStorage.getItem(USER_KEY);
      if (storedToken && storedUser) {
        const parsed = JSON.parse(storedUser) as ProUser;
        // Sessions minted before the `/api/user/me` 404 fix carry an empty
        // userId, which breaks project ownership and analytics identity. They
        // can't be repaired in place (the refresh endpoint returns tokens
        // only), so drop them and make the user sign in once more.
        if (!parsed?.userId) {
          throw new Error("stale session: missing userId");
        }
        setAccessToken(storedToken);
        setUser(parsed);
      }
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_KEY);
      localStorage.removeItem(USER_KEY);
    }
    setIsLoading(false);
  }, []);

  // Analytics: identify the Pro user + set their retailer Group (PLA2-366).
  // Fires on login AND on session-restore (both set `user`). No-ops if PostHog
  // isn't configured.
  useEffect(() => {
    if (!user) return;
    identifyPro(user.userId, {
      email: user.email,
      retailerId: user.retailerId,
      retailerName: user.retailerName,
    });
    setRetailerGroup(user.retailerId, {
      name: user.retailerName,
      accountType: "pro",
    });
  }, [user]);

  // Redirect unauthenticated users away from protected routes
  useEffect(() => {
    if (isLoading) return;
    const isLoginPage = pathname === "/pro/login" || pathname === "/pro/signup";
    if (!user && !isLoginPage) {
      router.replace("/pro/login");
    }
    if (user && isLoginPage) {
      router.replace("/pro");
    }
  }, [user, isLoading, pathname, router]);

  const refreshAccessToken = useCallback(async (): Promise<string | null> => {
    // Deduplicate concurrent refresh calls
    if (refreshPromiseRef.current) return refreshPromiseRef.current;

    refreshPromiseRef.current = (async () => {
      const refreshToken = localStorage.getItem(REFRESH_KEY);
      if (!refreshToken) return null;

      try {
        const res = await fetch("/api/pro/auth/refresh", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });

        if (!res.ok) return null;

        const data = await res.json();
        const newAccessToken = data.access_token;
        if (data.refresh_token) {
          localStorage.setItem(REFRESH_KEY, data.refresh_token);
        }
        localStorage.setItem(TOKEN_KEY, newAccessToken);
        setAccessToken(newAccessToken);
        return newAccessToken;
      } catch {
        return null;
      } finally {
        refreshPromiseRef.current = null;
      }
    })();

    return refreshPromiseRef.current;
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await fetch("/api/pro/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || "Invalid credentials");
    }

    const data = await res.json();
    const { access_token, refresh_token, user: userData } = data;

    localStorage.setItem(TOKEN_KEY, access_token);
    localStorage.setItem(REFRESH_KEY, refresh_token);
    localStorage.setItem(USER_KEY, JSON.stringify(userData));

    setAccessToken(access_token);
    setUser(userData);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(USER_KEY);
    setAccessToken(null);
    setUser(null);
    // Must fire BEFORE resetAnalytics() — after the reset the event would be
    // attributed to a brand-new anonymous person instead of the Pro user.
    capture(ANALYTICS_EVENTS.PRO_LOGOUT);
    resetAnalytics(); // unlink the device from the identified Pro user
    document.cookie =
      "pro_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
    router.replace("/pro/login");
  }, [router]);

  const getAuthHeaders = useCallback((): Record<string, string> => {
    if (!accessToken) return {};
    return { Authorization: `Bearer ${accessToken}` };
  }, [accessToken]);

  const fetchWithAuth = useCallback(
    async (url: string, options: RequestInit = {}): Promise<Response> => {
      const currentToken = localStorage.getItem(TOKEN_KEY);
      const headers = new Headers(options.headers);
      if (currentToken) {
        headers.set("Authorization", `Bearer ${currentToken}`);
      }

      const res = await fetch(url, { ...options, headers });

      if (res.status === 401) {
        const newToken = await refreshAccessToken();
        if (!newToken) {
          logout();
          return res;
        }
        headers.set("Authorization", `Bearer ${newToken}`);
        return fetch(url, { ...options, headers });
      }

      return res;
    },
    [refreshAccessToken, logout]
  );

  return (
    <ProAuthContext.Provider
      value={{
        user,
        accessToken,
        isLoading,
        isAuthenticated: !!user,
        login,
        logout,
        getAuthHeaders,
        fetchWithAuth,
      }}
    >
      {children}
    </ProAuthContext.Provider>
  );
}

export function useProAuth(): ProAuthContextValue {
  const context = useContext(ProAuthContext);
  if (!context) {
    throw new Error("useProAuth must be used within a ProAuthProvider");
  }
  return context;
}
